import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { PropertyAccessChange } from 'lumen/components/settings/users/modals/modal-property-access';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class HotelSettingsUsersPropertyAccessController extends Controller {
  @tracked declare model: Userhotelrelationship[];

  queryParams = ['userId', 'userEmail'];

  @service('ajax') declare ajaxService: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  userEmail = '';
  userId = '';

  get isOwnUser(): boolean {
    return this.userId === this.hotelAndMarketService.user?.id;
  }

  private async reloadRelationships(): Promise<void> {
    this.store.unloadAll('userhotelrelationship');

    const results = await this.store.query('userhotelrelationship', {
      user_id: this.userId,
      page: 0,
    });

    this.model = results.slice() as unknown as Userhotelrelationship[];
  }

  @action
  close(): void {
    this.router.transitionTo('hotel.settings.users');
  }

  @action
  async saveChanges(changes: PropertyAccessChange[]): Promise<boolean> {
    if (this.isOwnUser) {
      const confirmed = await this.alerting.showConfirmWarningAlert({
        text: this.intl.t('settings.hotel_access.confirm_edit_self'),
      });

      if (!confirmed) {
        return false;
      }
    }

    try {
      for (const change of changes) {
        await this.ajaxService.request('userhotelrelationships/', {
          method: 'PUT',
          data: {
            subscription_ids: change.subscriptionIds,
            user_id: change.userId,
            ...(change.accessRight && { access_right: change.accessRight }),
            ...(change.accessibleProducts && {
              accessible_products: change.accessibleProducts,
            }),
          },
        });
      }

      this.toast.success(this.intl.t('settings.users.bulk_edit.success'), {
        showIcon: true,
      });

      await this.reloadRelationships();
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.bulk_edit.failure'), {
        showIcon: true,
      });
      await this.reloadRelationships();
      return false;
    }
  }
}
